import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useAuth } from '@/features/auth/auth-context'
import { acceptInvitation, getInvitationByToken } from '@/features/settings/invitations-api'
import { queryClient } from '@/shared/api/query-client'
import { Button } from '@/shared/components/ui/button'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { LoadingBlock } from '@/shared/components/common/loading-block'

export function InviteAcceptPage() {
  const { token = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, refreshUser } = useAuth()
  const [accepting, setAccepting] = useState(false)

  const invitationQuery = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => getInvitationByToken(token),
    retry: false,
    enabled: token.length > 0,
  })

  if (invitationQuery.isLoading) {
    return <LoadingBlock label="Загружаем приглашение..." />
  }

  const invitation = invitationQuery.data
  const notFound = invitationQuery.isError || !invitation
  const emailMatches =
    Boolean(user?.email) && invitation?.email?.toLowerCase() === user?.email?.toLowerCase()

  const onAccept = async () => {
    setAccepting(true)
    try {
      await acceptInvitation(token)
      await refreshUser()
      // Активная организация сменилась — сбрасываем кэш прошлого контекста.
      queryClient.clear()
      toast.success('Вы присоединились к организации')
      navigate('/projects')
    } catch {
      // toast об ошибке покажет глобальный перехватчик apiClient
    } finally {
      setAccepting(false)
    }
  }

  return (
    <div className="mx-auto mt-16 max-w-md px-4">
      <Card>
        <CardHeader>
          <CardTitle>Приглашение в организацию</CardTitle>
          <CardDescription>
            {notFound
              ? 'Приглашение не найдено.'
              : invitation?.project_name
                ? `«${invitation.organization_name ?? 'Организация'}» приглашает вас (${invitation.email}) к работе над проектом «${invitation.project_name}».`
                : `«${invitation?.organization_name ?? 'Организация'}» приглашает вас (${invitation?.email}).`}
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {!notFound && invitation?.status !== 'pending' ? (
            <p className="text-sm text-[var(--color-text-soft)]">Приглашение уже неактивно.</p>
          ) : null}

          {!notFound && invitation?.expired ? (
            <p className="text-sm text-[var(--color-text-soft)]">Срок приглашения истёк.</p>
          ) : null}

          {!notFound && invitation?.status === 'pending' && !invitation?.expired ? (
            isAuthenticated ? (
              emailMatches ? (
                <Button loading={accepting} onClick={() => void onAccept()}>
                  Принять приглашение
                </Button>
              ) : (
                <p className="text-sm text-[var(--color-text-soft)]">
                  Вы вошли под другим email. Войдите под {invitation?.email}, чтобы принять.
                </p>
              )
            ) : (
              <div className="flex gap-2">
                <Link to="/login" state={{ from: location }}>
                  <Button variant="secondary">Войти</Button>
                </Link>
                <Link to="/register" state={{ from: location }}>
                  <Button>Регистрация</Button>
                </Link>
              </div>
            )
          ) : null}
        </CardBody>
      </Card>
    </div>
  )
}
